import React, {useCallback, useMemo} from 'react';
import {useTranslation} from 'next-i18next';
import {
	Avatar,
	Popover,
	PopoverBody,
	PopoverContent,
	PopoverTrigger,
	Portal,
	useDisclosure,
	VStack,
} from '@tempo/core';
import {ChevronRightIcon} from '@tempo/icons';
import UserMenuItem from './UserMenuItem';
import ArtistPictureList from './ArtistPictureList';

import {
	isArtistManager,
	isArtistMarketing,
	isUserApproved,
	isLabelGroup,
	isProvider,
} from '@deezer/next/shared/utils/user';
import {useProfiles, useUser} from '@deezer/next/contexts/main/hooks';
import {Routes} from '@constants/routes';
import NavItem from '@components/Sidebar/NavItem';

type UserMenuProps = {
	onMenuItemClick?: () => void;
	isMobile?: boolean;
};

const UserMenu = ({onMenuItemClick, isMobile}: UserMenuProps) => {
	const {t} = useTranslation('d4c');
	const {isOpen, onOpen, onClose} = useDisclosure();
	const user = useUser();
	const profiles = useProfiles();

	const userApproved = isUserApproved(user);

	const handleMenuItemClick = useCallback(() => {
		if (onMenuItemClick) {
			onMenuItemClick();
		}
		onClose();
	}, [onMenuItemClick, onClose]);

	const content = useMemo(() => {
		return (
			<PopoverContent maxWidth="calc(100vw - 32px)">
				<PopoverBody pt="spacing.s" pb="spacing.l" px={0}>
					<VStack alignItems="initial" spacing="spacing.xs">
						{isUserApproved(user) && (
							<>
								<UserMenuItem
									href={Routes.MY_PROFILE}
									label={t('profilesettings_text_editmyprofile_d4c-web')}
									icon={<ChevronRightIcon />}
									onClick={handleMenuItemClick}
								/>

								{isLabelGroup(user) && (
									<UserMenuItem
										href={Routes.SUB_LABELS}
										label={t('sublabelspage_title_manageyoursublabels_d4c-web')}
										icon={<ChevronRightIcon />}
										onClick={handleMenuItemClick}
									/>
								)}

								{(isProvider(user) || isArtistMarketing(user)) && (
									<UserMenuItem
										href={Routes.PROVIDER_POLICY}
										// fixme translate
										label="Provider Policy"
										icon={<ChevronRightIcon />}
										onClick={handleMenuItemClick}
									/>
								)}

								{(isLabelGroup(user) || isArtistManager(user)) && (
									<UserMenuItem
										href={Routes.REQUEST_MONITORING}
										label={t(
											'profilesettings_text_monitoryourrequests_d4c-web',
										)}
										icon={<ChevronRightIcon />}
										onClick={handleMenuItemClick}
									/>
								)}

								{isArtistManager(user) && profiles.length > 1 && (
									<UserMenuItem
										href={Routes.PROFILES}
										label={t('profilesettings_text_switchartist_d4c-web')}
										icon={<ChevronRightIcon />}
										onClick={handleMenuItemClick}
									>
										<ArtistPictureList />
									</UserMenuItem>
								)}
							</>
						)}
					</VStack>
				</PopoverBody>
			</PopoverContent>
		);
	}, [handleMenuItemClick, profiles.length, t, user]);

	if (!user) {
		return null;
	}

	if (!userApproved) {
		return (
			<NavItem
				label={user.name}
				px="spacing.s"
				iconElement={
					<Avatar
						name={user.name}
						size="size.s"
						bg="background.accent.primary.default"
						color="text.accent.onDark.default"
					/>
				}
			/>
		);
	}

	return (
		<Popover
			returnFocusOnClose={false}
			isOpen={isOpen}
			onClose={onClose}
			placement="top-start"
		>
			<PopoverTrigger>
				<NavItem
					label={user.name}
					onClick={onOpen}
					px="spacing.s"
					iconElement={
						<Avatar
							name={user.name}
							size="size.s"
							bg="background.accent.primary.default"
							color="text.accent.onDark.default"
						/>
					}
				/>
			</PopoverTrigger>
			{isMobile ? content : <Portal>{content}</Portal>}
		</Popover>
	);
};

export default UserMenu;
